import { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { useLoading } from '../../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { ArrowUpTrayIcon, PencilIcon } from "@heroicons/react/24/outline";
import {
    capitalizeWords, isValidPhone, encryptPayload, formatCnic,
    formatXX, formatXXSets, isValidCnic, validateImageFiles,
    formatPhoneSets,
    toDateTimeIso
} from '../../../../constants/helpers';
import {
    genderOptions, relationOptions, religionOptions, kidGenderOptions, educationOptions, marriedStatusOptions,
    parentStatusOptions, categoryOptions, countriesOptions, employeeMediaRoles, guarantorMediaRoles, jobTypeOptions
} from '../../../../constants/options';
import { compressMedia } from "../../../../utils/media/mediaCompression";
import LoaderBar from '../../../../components/globalloaders/LoaderBar';
import ConfirmDialog from '../../../../components/dialogbox/ConfirmDialog';
import api from '../../../../utils/auth/api';
import EmployeeSign from './EmployeeSign';
import EmployeeCrop from './EmployeeCrop';

const EmployeeAdd = ({ open }) => {

    const { isLoading, setIsLoading } = useLoading();
    const objectUrlsRef = useRef(new Set());

    // State for loading bar with percentage
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [progressLabel, setProgressLabel] = useState("");

    const [dialog, setDialog] = useState({
        open: false,
        type: "info",
        title: "",
        message: "",
        confirmText: "",
        action: () => { },
    });

    // === Add DialogBox Code & Store API ===
    const initialEmployeeState = {
        category: "",
        date: "",
        duty_time: "",

        name: "",
        father_husband_name: "",
        grandfather_name: "",
        parent_status: "",
        gender: "",
        age: "",
        education: "",
        experience: "",
        cnic_no: "",
        countryCode: "PK",
        phone: "",
        otherPhone: "",
        religion: "",
        caste: "",
        known_place: "",
        address: "",

        married_status: "",
        kid_gender: "",
        kid_amount: "",
        kid_age: "",
        media: [],
    };
    const initialGuarantorState = {
        name: "",
        father_husband_name: "",
        gender: "",
        age: "",
        cnic_no: "",
        countryCode: "PK",
        phone: "",
        address: "",
        relation: "",
        religion: "",
        caste: "",
        occupation: "",
        media: [],
        allow: false, // for input visibility
        checked: false, // for check btn
    };
    const [employee, setEmployee] = useState(initialEmployeeState);
    const [guarantors, setGuarantors] = useState([initialGuarantorState]);

    // Employee check btn & input visibility control
    const [employeeExist, setEmployeeExist] = useState(false);
    const [employeeChecked, setEmployeeChecked] = useState(false);

    // Close & Reset All States
    const handleAddClose = () => {
        objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        objectUrlsRef.current.clear();
        setEmployee(initialEmployeeState);
        setGuarantors([initialGuarantorState]);
        setEmployeePreviews({});
        setGuarantorPreviews({});
        setShowMoreDocs(false);
        setEmployeeExist(false);
        setEmployeeChecked(false);
    };

    // Employee & Guarantor Image Preview & Upload UI
    const MediaPreview = ({ items = [], onRemove }) => {
        if (!items.length) {
            return (
                <p className="text-sm text-gray-500 italic mt-3">
                    No documents uploaded
                </p>
            );
        };
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 mt-3">
                {items.map((m) => (
                    <div
                        key={m.uid}
                        className="relative group rounded-xl overflow-hidden border bg-gray-50 shadow-sm"
                    >
                        <img
                            src={m.url}
                            alt=""
                            className="w-full h-44 object-cover"
                        />

                        <button
                            type="button"
                            onClick={() => onRemove(m.uid)}
                            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/70 text-white text-xs flex items-center justify-center"
                        >
                            ✕
                        </button>
                    </div>
                ))}
            </div>
        );
    };
    const UploadCard = ({ onChange, disabled }) => (
        <label
            className={`inline-flex items-center border border-dashed border-violet-300 gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition
                ${disabled
                    ? "opacity-50 cursor-not-allowed text-gray-500"
                    : "text-violet-600 hover:bg-violet-50 cursor-pointer"
                }
        `}
        >
            <ArrowUpTrayIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Upload</span>
            <input
                type="file"
                accept="image/*"
                disabled={disabled}
                className="hidden"
                onChange={(e) => onChange(e.target.files)}
            />
        </label>
    );

    // Employee Text/Drop-Down Select Input handlers
    const handleSelectEmployee = (selectedOption, { name }) => {
        setEmployee({
            ...employee,
            [name]: selectedOption ? selectedOption.value : "",
        });
    };
    const onEmployeeChange = (e) => {
        const { name, value } = e.target;
        const autoCapFields = ["name", "father_husband_name", "grandfather_name", "caste", "known_place", "address"];
        setEmployee({
            ...employee,
            [name]: autoCapFields.includes(name) ? capitalizeWords(value) : value,
        });
    };
    // Employee Image select input handler states
    const PRIMARY_EMPLOYEE_DOCS = ["profile", "cnic_front", "cnic_back", "fingerprint", "signature"];
    const EMPLOYEE_DOC_LIMITS = {
        profile: 1,
        cnic_front: 1,
        cnic_back: 1,
        fingerprint: 1,
        signature: 1,
        bform: 1,
        driving_license_front: 1,
        driving_license_back: 1,
    };
    const [employeePreviews, setEmployeePreviews] = useState({});
    const [showMoreDocs, setShowMoreDocs] = useState(false);
    const handleEmployeeMediaChange = async (files, role) => {
        if (!files?.length) return;

        // images only (hard guard)
        const { valid, errors } = validateImageFiles(files);
        if (errors.length) {
            errors.forEach(msg => toast.warning(msg));
        }
        if (!valid.length) return;
        const imageFiles = valid;

        const existingCount = employee.media.filter(m => m.role === role).length;
        const maxAllowed = EMPLOYEE_DOC_LIMITS[role] ?? 1;
        const remainingSlots = maxAllowed - existingCount;

        if (remainingSlots <= 0) return;

        const acceptedFiles = imageFiles.slice(0, remainingSlots);

        try {
            setIsUploading(true);
            setProgressLabel("Compressing images...");
            setUploadProgress(0);

            const compressedFiles = [];

            for (let i = 0; i < acceptedFiles.length; i++) {
                const file = acceptedFiles[i];

                const compressed = await compressMedia(
                    file,
                    "image",
                    (progress) => {
                        // normalize progress across multiple files
                        const overallProgress =
                            ((i + progress / 100) / acceptedFiles.length) * 100;
                        setUploadProgress(Math.round(overallProgress));
                    }
                );

                compressedFiles.push(compressed);
            }

            // previews
            const previews = compressedFiles.map((file) => {
                const uid = crypto.randomUUID();
                const url = URL.createObjectURL(file);
                objectUrlsRef.current.add(url);
                return {
                    uid,
                    file,
                    url,
                    name: file.name,
                    type: "image",
                }
            });

            setEmployeePreviews((prev) => ({
                ...prev,
                [role]: [...(prev[role] || []), ...previews],
            }));

            // persist media
            const newMedia = previews.map(({ uid, file, name }) => ({
                uid,
                file,
                role,
                type: "image",
                name,
            }));


            setEmployee((prev) => ({
                ...prev,
                media: [...prev.media, ...newMedia],
            }));
        } catch (err) {
            console.error("Employee media compression failed:", err);
        } finally {
            setIsUploading(false);
            setProgressLabel("");
            setUploadProgress(0);
        }
    };
    const removeEmployeeMedia = (role, uid) => {
        setEmployeePreviews((prev) => {
            const item = prev[role]?.find(m => m.uid === uid);
            if (item?.url) {
                URL.revokeObjectURL(item.url);
                objectUrlsRef.current.delete(item.url);
            }
            return {
                ...prev,
                [role]: prev[role].filter(m => m.uid !== uid),
            }
        });

        setEmployee((prev) => ({
            ...prev,
            media: prev.media.filter(m => m.uid !== uid),
        }));
    };
    const clearExtraEmployeeDocs = () => {
        setEmployeePreviews(prev => {
            const cleaned = {};
            Object.keys(prev).forEach(role => {
                if (PRIMARY_EMPLOYEE_DOCS.includes(role)) {
                    cleaned[role] = prev[role];
                }
            });
            return cleaned;
        });

        setEmployee(prev => ({
            ...prev,
            media: prev.media.filter(m =>
                PRIMARY_EMPLOYEE_DOCS.includes(m.role)
            ),
        }));
    };

    // Guarantor Text Input handlers & Add/Remove States
    const onGuarantorChange = (index, e) => {
        const { name, value } = e.target;
        const autoCapFields = ["name", "father_husband_name", "caste", "address", "occupation"];
        setGuarantors(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [name]: autoCapFields.includes(name)
                    ? capitalizeWords(value)
                    : value,
            };
            return updated;
        });
    };
    const addGuarantor = () => {
        setGuarantors([...guarantors, initialGuarantorState]);
    };
    const removeGuarantor = (index) => {
        setGuarantors(guarantors.filter((_, i) => i !== index));
    };
    // Guarantor Image select input handler states
    const GUARANTOR_DOC_LIMITS = {
        cnic_front: 1,
        cnic_back: 1,
        fingerprint: 1,
        signature: 1,
    };
    const [guarantorPreviews, setGuarantorPreviews] = useState({});
    const handleGuarantorMediaChange = async (gIndex, files, role) => {
        if (!files?.length) return;

        // images only (hard guard)
        const { valid, errors } = validateImageFiles(files);
        if (errors.length) {
            errors.forEach(msg => toast.warning(msg));
        }
        if (!valid.length) return;
        const imageFiles = valid;

        const currentCount = guarantors[gIndex]?.media?.filter(m => m.role === role).length || 0;
        const maxAllowed = GUARANTOR_DOC_LIMITS[role] ?? 1;
        const remainingSlots = maxAllowed - currentCount;
        if (remainingSlots <= 0) return;

        const acceptedFiles = imageFiles.slice(0, remainingSlots);

        try {
            setIsUploading(true);
            setProgressLabel("Compressing images...");
            setUploadProgress(0);

            const compressedFiles = [];

            for (let i = 0; i < acceptedFiles.length; i++) {
                const compressed = await compressMedia(
                    acceptedFiles[i],
                    "image",
                    (progress) => {
                        const overall =
                            ((i + progress / 100) / acceptedFiles.length) * 100;
                        setUploadProgress(Math.round(overall));
                    }
                );
                compressedFiles.push(compressed);
            }

            // previews
            const previews = compressedFiles.map(file => {
                const uid = crypto.randomUUID();
                const url = URL.createObjectURL(file);
                objectUrlsRef.current.add(url);
                return {
                    uid,
                    file,
                    url,
                    name: file.name,
                    type: "image",
                };
            });

            setGuarantorPreviews(prev => ({
                ...prev,
                [gIndex]: {
                    ...(prev[gIndex] || {}),
                    [role]: [...(prev[gIndex]?.[role] || []), ...previews],
                },
            }));

            // persist in guarantor.media
            setGuarantors(prev => {
                const updated = [...prev];
                updated[gIndex].media = [
                    ...(updated[gIndex].media || []),
                    ...previews.map(({ uid, file, name }) => ({
                        uid,
                        file,
                        role,
                        type: "image",
                        name,
                    })),
                ];
                return updated;
            });
        } catch (err) {
            console.error("Guarantor media compression failed:", err);
        } finally {
            setIsUploading(false);
            setProgressLabel("");
            setUploadProgress(0);
        }
    };
    const removeGuarantorMedia = (gIndex, role, uid) => {
        setGuarantorPreviews(prev => {
            const item = prev[gIndex]?.[role]?.find(m => m.uid === uid);
            if (item?.url) {
                URL.revokeObjectURL(item.url);
                objectUrlsRef.current.delete(item.url);
            }
            return {
                ...prev,
                [gIndex]: {
                    ...prev[gIndex],
                    [role]: prev[gIndex][role].filter(m => m.uid !== uid),
                },
            }
        });

        setGuarantors(prev => {
            const updated = [...prev];
            updated[gIndex].media = updated[gIndex].media.filter(
                m => m.uid !== uid
            );
            return updated;
        });
    };

    // Validators
    const validateEmployee = (e) => {
        if (!e.name) return "Employee name is required.";
        if (!e.gender) return "Employee gender is required.";
        if (!e.age) return "Employee age is required.";
        if (!e.cnic_no || !isValidCnic(e.cnic_no))
            return "Employee CNIC missing or invalid.";
        if (!e.countryCode || !e.phone ||
            !isValidPhone(e.phone, e.countryCode))
            return "Employee phone is missing or invalid.";
        if (!e.known_place) return "Employee known place is required.";
        if (!e.address) return "Employee address is required.";
        if (!e.father_husband_name)
            return "Father/Husband name is required.";
        if (!e.parent_status) return "Parent status is required.";
        if (!e.category) return "Employee category is required.";
        if (!e.date) return "Joining date is required.";
        if (!e.duty_time) return "Duty time is required.";
        if (!e.married_status) return "Married status is required.";
        if (!e.caste) return "Caste is required.";
        if (!e.religion) return "Religion is required.";
        if (!e.education) return "Education is required.";
        if (!e.experience) return "Experience is required.";

        return null; // valid
    };
    const validateEmployeeMedia = (employee) => {
        if (!Array.isArray(employee.media)) {
            return "Employee documents are missing.";
        }
        const countByRole = employee.media.reduce((acc, { role }) => {
            acc[role] = (acc[role] || 0) + 1;
            return acc;
        }, {});

        if (countByRole.profile !== 1) {
            return "Employee profile image is required.";
        }

        if (countByRole.cnic_front !== 1 || countByRole.cnic_back !== 1) {
            return "Employee CNIC front & back is required.";
        }

        if (countByRole.fingerprint !== 1) {
            return "Employee fingerprint is required.";
        }

        // ---- STRICT COUNT RULE ----
        for (const role of ["cnic_front", "cnic_back", "bform", "signature", "driving_license_front", "driving_license_back"]) {
            if ((countByRole[role] || 0) > 1) {
                return `Only one ${role.replace("_", " ")} image is allowed.`;
            }
        }

        return null;
    };
    const isGuarantorEmpty = (g) => {
        return (
            !g.cnic_no &&
            !g.phone &&
            !g.name &&
            !g.father_husband_name &&
            !g.address &&
            !g.relation &&
            !g.religion
        );
    };
    const validateGuarantors = (guarantors) => {
        if (!Array.isArray(guarantors) || guarantors.length === 0)
            return null;

        for (let i = 0; i < guarantors.length; i++) {
            const g = guarantors[i];
            const label = `Guarantor #${i + 1}`;

            if (!g.cnic_no || !isValidCnic(g.cnic_no))
                return `${label}: CNIC missing or invalid.`;

            // Only if guarantor is NEW
            if (g.allow === true) {
                if (!g.name) return `${label}: name is required.`;
                if (!g.father_husband_name) return `${label}: father/husband name is required.`;
                if (!g.gender) return `${label}: gender is required.`;
                if (!g.countryCode || !g.phone ||
                    !isValidPhone(g.phone, g.countryCode))
                    return `${label}: phone is missing or invalid.`;
                if (!g.religion) return `${label}: religion is required.`;
                if (!g.relation) return `${label}: relation is required.`;
            }
        }

        return null; // valid
    };

    // Signature-Pad
    const [signOpen, setSignOpen] = useState({
        open: false,
        target: null,
    });
    const handleSignatureComplete = async (file) => {
        if (!signOpen.target) return;
        if (signOpen.target.type === "employee") {
            await handleEmployeeMediaChange([file], "signature");
        }
        if (signOpen.target.type === "guarantor") {
            await handleGuarantorMediaChange(
                signOpen.target.index,
                [file],
                "signature"
            );
        }
        setSignOpen({ open: false, target: null });
    };

    // Card-Crop
    const CROP_REQUIRED_ROLES = [
        "profile",
        "cnic_front",
        "cnic_back",
        "driving_license_front",
        "driving_license_back",
        "bform",
        "fingerprint",
        "signature",
    ];
    const CROP_RATIOS = {
        profile: 1 / 2,
        cnic_front: 85.6 / 53.98,
        cnic_back: 85.6 / 53.98,
        driving_license_front: 85.6 / 53.98,
        driving_license_back: 85.6 / 53.98,
        bform: 210 / 297,
        signature: 1 / 1,
        fingerprint: 1 / 1,
    };
    const [cropState, setCropState] = useState({
        open: false,
        file: null,
        role: null,
        target: null, // { type: "employee" } OR { type: "guarantor", index }
    });
    const onEmployeeFileSelect = (files, role) => {
        if (!files?.length) return;
        const { valid, errors } = validateImageFiles(files);
        if (errors.length) {
            errors.forEach(msg => toast.warning(msg));
        }
        if (!valid.length) return;
        const file = valid[0];

        if (CROP_REQUIRED_ROLES.includes(role)) {
            setCropState({
                open: true,
                file,
                role,
                target: { type: "employee" },
            });
        } else {
            handleEmployeeMediaChange(files, role);
        }
    };
    const onGuarantorFileSelect = (index, files, role) => {
        if (!files?.length) return;
        const { valid, errors } = validateImageFiles(files);
        if (errors.length) {
            errors.forEach(msg => toast.warning(msg));
        }
        if (!valid.length) return;
        const file = valid[0];

        if (CROP_REQUIRED_ROLES.includes(role)) {
            setCropState({
                open: true,
                file,
                role,
                target: { type: "guarantor", index },
            });
        } else {
            handleGuarantorMediaChange(index, files, role);
        }
    };
    const handleCropComplete = async (croppedFile) => {
        const { role, target } = cropState;

        if (target.type === "employee") {
            await handleEmployeeMediaChange([croppedFile], role);
        }

        if (target.type === "guarantor") {
            await handleGuarantorMediaChange(
                target.index,
                [croppedFile],
                role
            );
        }

        setCropState({
            open: false,
            file: null,
            role: null,
            target: null,
        });
    };

    // Check Employee & Guarantor Cnic API
    const checkCnic = async ({ type, cnic_no, index = null }) => {
        if (!cnic_no || !isValidCnic(cnic_no)) {
            toast.warning("Cnic missing or invalid");
            return;
        }
        if (isLoading) return;
        setIsLoading(true);
        try {
            const res = await api.get(`${process.env.REACT_APP_URI}/employee/check-cnic/${type}/${cnic_no}`);
            const exists = res.data.exists;

            // EMPLOYEE
            if (type === "employee") {
                if (exists) {
                    setEmployeeExist(false);
                    setEmployeeChecked(false);
                    toast.warning("Employee already exists.");
                } else {
                    setEmployeeExist(true);
                    setEmployeeChecked(true);
                    toast.info("Employee not exists. Continue.");
                }
            }
            // GUARANTOR
            if (type === "guarantor" && index !== null) {
                setGuarantors(prev => {
                    const updated = [...prev];
                    updated[index].allow = !exists;
                    updated[index].checked = true;
                    return updated;
                });
                exists
                    ? toast.warning("Guarantor already exists. Will be linked.")
                    : toast.info("Guarantor not exists. Continue.");
            }
        } catch (err) {
            console.error("Error checking cnic of employee or guarantor:", err);
            toast.error(err?.response.data.message || "Unable to check Cnic.");
        } finally {
            setIsLoading(false);
        }
    };
    // Store API handler
    const handleSubmit = async () => {

        if (isLoading) return;

        const employeeError = validateEmployee(employee);
        if (employeeError) {
            toast.warning(employeeError);
            return;
        }

        const employeeMediaError = validateEmployeeMedia(employee);
        if (employeeMediaError) {
            toast.warning(employeeMediaError);
            return;
        }

        const cleanGuarantors = Array.isArray(guarantors)
            ? guarantors.filter(g => !isGuarantorEmpty(g))
            : [];

        const guarantorError = validateGuarantors(cleanGuarantors);
        if (guarantorError) {
            toast.warning(guarantorError);
            return;
        }

        const formData = new FormData();
        formData.append(
            "data",
            encryptPayload({
                employee: {
                    ...employee,
                    date: toDateTimeIso(employee.date),
                    media: employee.media.map(({ uid, role, type, name }) => ({
                        role, type, name, uid
                    }))
                },
                guarantors: cleanGuarantors.map(g => ({
                    ...g,
                    media: g.media.map(({ uid, role, type, name }) => ({
                        role, type, name, uid
                    }))
                }))
            })
        );
        // employee files
        employee.media.forEach((m) => {
            formData.append(`employee_media_${m.uid}`, m.file);
        });
        // guarantor files
        cleanGuarantors.forEach((g, gi) => {
            g.media.forEach((m) => {
                formData.append(`guarantor_${gi}_media_${m.uid}`, m.file);
            });
        });

        setIsLoading(true);
        try {
            const response = await api.post(`${process.env.REACT_APP_URI}/employee/create`, formData, { headers: { "Content-Type": "multipart/form-data" } });
            if (response.data.success) {
                toast.success(response.data.message);
                handleAddClose();
            }
            else {
                toast.error(response.data.message);
            }
        }
        catch (error) {
            console.log("Employee Creation failed:", error);
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    data.errors.forEach(err => toast.warning(err));
                } else {
                    toast.error(data.message);
                }
            } else {
                toast.error("Unable to Create Employee Network Error, please try again!");
            }
        }
        finally {
            setIsLoading(false);
            return;
        }
    };

    // For image preview memory cleanup
    useEffect(() => {
        const urls = objectUrlsRef.current;
        return () => {
            urls.forEach((url) => {
                URL.revokeObjectURL(url);
            });
            urls.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!open) return null;

    return (
        <>
            <LoaderBar
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                progressLabel={progressLabel}
            />
            <ConfirmDialog
                isOpen={dialog.open}
                onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onConfirm={dialog.action}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                type={dialog.type}
            />
            <EmployeeSign
                open={signOpen.open}
                onClose={() => setSignOpen({ open: false, target: null })}
                onComplete={handleSignatureComplete}
            />
            <EmployeeCrop
                open={cropState.open}
                file={cropState.file}
                aspect={CROP_RATIOS[cropState.role]}
                onClose={() =>
                    setCropState({
                        open: false,
                        file: null,
                        role: null,
                        target: null,
                    })
                }
                onComplete={handleCropComplete}
            />
            <div className="w-full max-h-[85vh] bg-white rounded-2xl border border-gray-200 p-3 space-y-4">
                <div className='flex flex-col items-center'>
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
                        Create Employee
                    </h2>
                    <p className="text-sm text-gray-500">
                        Add new employee details below.
                    </p>
                </div>
                <div className="max-h-[65vh] overflow-y-auto p-1">
                    {/* Employee & Guarantor Info & Documents */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Cnic #</label>
                            <input
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                placeholder="XXXXX-1234567-X"
                                type="tel"
                                inputMode="numeric"
                                name="cnic_no"
                                value={employee.cnic_no}
                                maxLength={15}
                                onChange={(e) => {
                                    setEmployee({ ...employee, cnic_no: formatCnic(e.target.value) });
                                    setEmployeeExist(false);
                                    setEmployeeChecked(false);
                                }}
                            />
                        </div>
                        {employeeExist && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Category</label>
                                    <Select
                                        name="category"
                                        options={categoryOptions}
                                        placeholder="Select Category"
                                        value={categoryOptions.find(option => option.value === employee.category)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'category' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Job Type</label>
                                    <Select
                                        name="duty_time"
                                        options={jobTypeOptions}
                                        placeholder="Select Job Type"
                                        value={jobTypeOptions.find(option => option.value === employee.duty_time)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'duty_time' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        type="datetime-local"
                                        name='date'
                                        value={employee.date}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="Full Name"
                                        type="text"
                                        name='name'
                                        value={employee.name}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Father/Husband Name</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="Full Name"
                                        type="text"
                                        name='father_husband_name'
                                        value={employee.father_husband_name}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Grandfather Name</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="Full Name"
                                        type="text"
                                        name='grandfather_name'
                                        value={employee.grandfather_name}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Parent Status</label>
                                    <Select
                                        name="parent_status"
                                        options={parentStatusOptions}
                                        placeholder="Select Status"
                                        value={parentStatusOptions.find(option => option.value === employee.parent_status)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'parent_status' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                                    <Select
                                        name="gender"
                                        options={genderOptions}
                                        placeholder="Select Gender"
                                        value={genderOptions.find(option => option.value === employee.gender)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'gender' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Age</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="XX year"
                                        type="text"
                                        name='age'
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={employee.age}
                                        onChange={(e) =>
                                            onEmployeeChange({
                                                target: {
                                                    name: e.target.name,
                                                    value: formatXX(e.target.value),
                                                },
                                            })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Education</label>
                                    <Select
                                        name="education"
                                        options={educationOptions}
                                        placeholder="Select Education"
                                        value={educationOptions.find(option => option.value === employee.education)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'education' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Experience</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="XX year"
                                        type="text"
                                        name='experience'
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={employee.experience}
                                        onChange={(e) =>
                                            onEmployeeChange({
                                                target: {
                                                    name: e.target.name,
                                                    value: formatXX(e.target.value),
                                                },
                                            })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                                        <select
                                            value={employee.countryCode}
                                            onChange={(e) =>
                                                setEmployee({
                                                    ...employee,
                                                    countryCode: e.target.value,
                                                    phone: "",
                                                })
                                            }
                                            className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                                        >
                                            {countriesOptions.map((c) => (
                                                <option key={c.code} value={c.code}>
                                                    {c.code} {c.dial}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            placeholder="Phone number"
                                            value={employee.phone}
                                            onChange={(e) => {
                                                const digits = e.target.value.replace(/\D/g, "");
                                                const selectedCountry = countriesOptions.find(
                                                    (c) => c.code === employee.countryCode
                                                );
                                                const maxLength = selectedCountry?.maxLength || 15;
                                                setEmployee({
                                                    ...employee,
                                                    phone: digits.slice(0, maxLength),
                                                });
                                            }}
                                            className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Other Phone</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        type="text"
                                        placeholder='031XXXXXXXX, 031XXXXXXXX...'
                                        name='otherPhone'
                                        inputMode="numeric"
                                        pattern="[0-9, ]*"
                                        value={employee.otherPhone}
                                        onChange={(e) =>
                                            onEmployeeChange({
                                                target: {
                                                    name: e.target.name,
                                                    value: formatPhoneSets(e.target.value),
                                                },
                                            })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Religion</label>
                                    <Select
                                        name="religion"
                                        options={religionOptions}
                                        placeholder="Select Religion"
                                        value={religionOptions.find(option => option.value === employee.religion)}
                                        onChange={(selectedOption) =>
                                            handleSelectEmployee(selectedOption, { name: 'religion' })
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Caste</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="Caste..."
                                        type="text"
                                        name='caste'
                                        value={employee.caste}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Known Place</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="Place near address..."
                                        type="text"
                                        name='known_place'
                                        value={employee.known_place}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Address</label>
                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                        placeholder="H#01, ST#01, ABC..."
                                        type="text"
                                        name='address'
                                        value={employee.address}
                                        onChange={onEmployeeChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Married Status</label>
                                    <Select
                                        name="married_status"
                                        options={marriedStatusOptions}
                                        placeholder="Select Status"
                                        value={marriedStatusOptions.find(option => option.value === employee.married_status)}
                                        onChange={(selectedOption) => {
                                            const value = selectedOption ? selectedOption.value : "";
                                            setEmployee(prev => ({
                                                ...prev,
                                                married_status: value,
                                                ...(value !== "married"
                                                    ? {
                                                        kid_amount: "",
                                                        kid_gender: "",
                                                        kid_age: "",
                                                    }
                                                    : {}),
                                            }));
                                        }}
                                    />
                                </div>

                                {employee.married_status === "married" && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Kid Amount</label>
                                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                type="text"
                                                placeholder='XX'
                                                name='kid_amount'
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={employee.kid_amount}
                                                onChange={(e) =>
                                                    onEmployeeChange({
                                                        target: {
                                                            name: e.target.name,
                                                            value: formatXX(e.target.value),
                                                        },
                                                    })
                                                }
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Kid Gender</label>
                                            <Select
                                                name="kid_gender"
                                                options={kidGenderOptions}
                                                placeholder="Select Gender"
                                                value={kidGenderOptions.find(option => option.value === employee.kid_gender)}
                                                onChange={(selectedOption) =>
                                                    handleSelectEmployee(selectedOption, { name: 'kid_gender' })
                                                }
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Kid Age</label>
                                            <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                type="text"
                                                placeholder='XX, XX, XX...'
                                                name='kid_age'
                                                inputMode="numeric"
                                                pattern="[0-9, ]*"
                                                value={employee.kid_age}
                                                onChange={(e) =>
                                                    onEmployeeChange({
                                                        target: {
                                                            name: e.target.name,
                                                            value: formatXXSets(e.target.value),
                                                        },
                                                    })
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {!employeeChecked && (
                        <div className="flex justify-end">
                            <button
                                className="mt-2 px-3 py-1.5 rounded-lg text-sm sm:text-base bg-green-600 text-white hover:bg-green-700 transition"
                                type="button"
                                onClick={() =>
                                    checkCnic({
                                        type: "employee",
                                        cnic_no: employee.cnic_no,
                                    })
                                }
                            >
                                Check
                            </button>
                        </div>
                    )}
                    {employeeExist && (
                        <>
                            <fieldset className="bg-white border rounded-xl p-3 mt-3">
                                <legend className="px-2 text-sm font-semibold text-gray-700">
                                    Employee Documents
                                </legend>
                                {/* PRIMARY DOCS */}
                                <div className="grid gap-6">
                                    {employeeMediaRoles
                                        .filter(
                                            (r) =>
                                                PRIMARY_EMPLOYEE_DOCS.includes(r.value) &&
                                                EMPLOYEE_DOC_LIMITS[r.value]
                                        )
                                        .map((r) => {
                                            const currentCount = employeePreviews[r.value]?.length || 0;
                                            const max = EMPLOYEE_DOC_LIMITS[r.value];

                                            return (
                                                <div key={r.value}>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {r.label} ({currentCount}/{max})
                                                        </span>

                                                        <div className="flex gap-2">
                                                            <UploadCard
                                                                disabled={currentCount >= max}
                                                                onChange={(files) =>
                                                                    onEmployeeFileSelect(files, r.value)
                                                                }
                                                            />

                                                            {r.value === "signature" && (
                                                                <button
                                                                    type="button"
                                                                    disabled={currentCount >= max}
                                                                    onClick={() => setSignOpen({
                                                                        open: true,
                                                                        target: { type: "employee" },
                                                                    })}
                                                                    className={`inline-flex items-center border border-dashed border-violet-300 gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition
                                                                                ${currentCount >= max
                                                                            ? "opacity-50 cursor-not-allowed text-gray-500"
                                                                            : "text-violet-600 hover:bg-violet-50 cursor-pointer"
                                                                        }
                                                                        `}
                                                                >
                                                                    <PencilIcon className="w-4 h-4" />
                                                                    <span className="hidden sm:inline">Sign</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <MediaPreview
                                                        items={employeePreviews[r.value]}
                                                        onRemove={(i) => removeEmployeeMedia(r.value, i)}
                                                    />
                                                </div>
                                            );
                                        })}
                                </div>
                                {/* TOGGLE */}
                                <div className="flex justify-end mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowMoreDocs(prev => {
                                                if (prev === true) {
                                                    clearExtraEmployeeDocs();
                                                }
                                                return !prev;
                                            });
                                        }}
                                        className="text-sm px-1.5 py-0.5 rounded-lg font-medium text-blue-600 hover:bg-blue-100"
                                    >
                                        {showMoreDocs ? "− Remove" : "+ Add more"}
                                    </button>
                                </div>
                                {/* EXTRA DOCS */}
                                {showMoreDocs && (
                                    <div className="grid gap-6 mt-4">
                                        {employeeMediaRoles
                                            .filter(
                                                (r) =>
                                                    !PRIMARY_EMPLOYEE_DOCS.includes(r.value) &&
                                                    EMPLOYEE_DOC_LIMITS[r.value]
                                            )
                                            .map((r) => {
                                                const currentCount = employeePreviews[r.value]?.length || 0;
                                                const max = EMPLOYEE_DOC_LIMITS[r.value];

                                                return (
                                                    <div key={r.value}>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {r.label} ({currentCount}/{max})
                                                            </span>

                                                            <UploadCard
                                                                disabled={currentCount >= max}
                                                                onChange={(files) =>
                                                                    onEmployeeFileSelect(files, r.value)
                                                                }
                                                            />
                                                        </div>

                                                        <MediaPreview
                                                            items={employeePreviews[r.value]}
                                                            onRemove={(i) => removeEmployeeMedia(r.value, i)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </fieldset>
                            {/* Guarantor Info and Documents */}
                            {guarantors.map((g, index) => (
                                <fieldset key={index} className="bg-white border p-3 rounded-lg mt-4">
                                    <legend className="text-md sm:text-lg font-semibold text-gray-700 px-2">Guarantor #{index + 1}</legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Cnic #</label>
                                            <input
                                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                placeholder="XXXXX-1234567-X"
                                                type="tel"
                                                inputMode="numeric"
                                                name="cnic_no"
                                                value={g.cnic_no}
                                                maxLength={15}
                                                onChange={(e) => {
                                                    const updated = [...guarantors];
                                                    updated[index].cnic_no = formatCnic(e.target.value);
                                                    updated[index].allow = false;
                                                    updated[index].checked = false;
                                                    setGuarantors(updated);
                                                }}
                                            />
                                        </div>
                                        {g.allow && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Full Name"
                                                        type="text"
                                                        name='name'
                                                        value={g.name}
                                                        onChange={(e) => onGuarantorChange(index, e)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Father/Husband Name</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Full Name"
                                                        type="text"
                                                        name='father_husband_name'
                                                        value={g.father_husband_name}
                                                        onChange={(e) => onGuarantorChange(index, e)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                                                    <Select
                                                        name="gender"
                                                        placeholder="Select Gender"
                                                        options={genderOptions}
                                                        value={genderOptions.find(o => o.value === g.gender)}
                                                        onChange={(opt) => onGuarantorChange(index, { target: { name: "gender", value: opt?.value } })}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Age</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="XX year"
                                                        type="text"
                                                        name='age'
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={g.age}
                                                        onChange={(e) =>
                                                            onGuarantorChange(index, {
                                                                target: {
                                                                    name: e.target.name,
                                                                    value: formatXX(e.target.value),
                                                                },
                                                            })
                                                        }
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                                    <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-400 transition">
                                                        <select
                                                            value={g.countryCode}
                                                            onChange={(e) => {
                                                                const updated = [...guarantors];
                                                                updated[index].countryCode = e.target.value;
                                                                updated[index].phone = "";
                                                                setGuarantors(updated);
                                                            }}
                                                            className="w-[88px] shrink-0 px-2 py-2 text-sm bg-transparent border-r border-gray-200 focus:outline-none cursor-pointer"
                                                        >
                                                            {countriesOptions.map((c) => (
                                                                <option key={c.code} value={c.code}>
                                                                    {c.code} {c.dial}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="tel"
                                                            inputMode="numeric"
                                                            placeholder="Phone number"
                                                            value={g.phone}
                                                            onChange={(e) => {
                                                                const digits = e.target.value.replace(/\D/g, "");
                                                                const max = countriesOptions.find(c => c.code === g.countryCode)?.maxLength || 15;
                                                                const updated = [...guarantors];
                                                                updated[index].phone = digits.slice(0, max);
                                                                setGuarantors(updated);
                                                            }}
                                                            className="flex-1 px-3 py-2 outline-none bg-transparent text-sm"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Address</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="H#01, ST#01, ABC..."
                                                        type="text"
                                                        name='address'
                                                        value={g.address}
                                                        onChange={(e) => onGuarantorChange(index, e)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Relation</label>
                                                    <Select
                                                        name="relation"
                                                        placeholder="Select Relation"
                                                        options={relationOptions}
                                                        value={relationOptions.find(o => o.value === g.relation)}
                                                        onChange={(opt) => onGuarantorChange(index, { target: { name: "relation", value: opt?.value } })}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Religion</label>
                                                    <Select
                                                        name="religion"
                                                        placeholder="Select Religion"
                                                        options={religionOptions}
                                                        value={religionOptions.find(o => o.value === g.religion)}
                                                        onChange={(opt) => onGuarantorChange(index, { target: { name: "religion", value: opt?.value } })}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Caste</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Caste"
                                                        type="text"
                                                        name='caste'
                                                        value={g.caste}
                                                        onChange={(e) => onGuarantorChange(index, e)}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Occupation</label>
                                                    <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Occupation..."
                                                        type="text"
                                                        name='occupation'
                                                        value={g.occupation}
                                                        onChange={(e) => onGuarantorChange(index, e)}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {!g.checked && (
                                        <div className="flex justify-end">
                                            <button
                                                className='mt-2 px-3 py-1.5 rounded-lg text-sm sm:text-base bg-green-600 text-white hover:bg-green-700 transition'
                                                type="button"
                                                onClick={() =>
                                                    checkCnic({
                                                        type: "guarantor",
                                                        cnic_no: g.cnic_no,
                                                        index,
                                                    })
                                                }
                                            >
                                                Check
                                            </button>
                                        </div>
                                    )}
                                    {g.allow && (
                                        <>
                                            <fieldset className="bg-gray-50 border rounded-xl p-3 mt-3">
                                                <legend className="text-sm font-semibold text-gray-600 px-2">
                                                    Documents
                                                </legend>
                                                <div className="grid gap-5">
                                                    {guarantorMediaRoles.map((r) => {
                                                        const currentCount =
                                                            guarantorPreviews[index]?.[r.value]?.length || 0;
                                                        const max = GUARANTOR_DOC_LIMITS[r.value];
                                                        return (
                                                            <div key={r.value}>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-medium text-gray-700">
                                                                        {r.label} ({currentCount}/{max})
                                                                    </span>

                                                                    <div className="flex gap-2">
                                                                        <UploadCard
                                                                            disabled={currentCount >= max}
                                                                            onChange={(files) =>
                                                                                onGuarantorFileSelect(index, files, r.value)
                                                                            }
                                                                        />

                                                                        {r.value === "signature" && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={currentCount >= max}
                                                                                onClick={() => setSignOpen({
                                                                                    open: true,
                                                                                    target: { type: "guarantor", index },
                                                                                })}
                                                                                className={`inline-flex items-center border border-dashed border-violet-300 gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition
                                                                                        ${currentCount >= max
                                                                                        ? "opacity-50 cursor-not-allowed text-gray-500"
                                                                                        : "text-violet-600 hover:bg-violet-50 cursor-pointer"
                                                                                    }
                                                                                `}
                                                                            >
                                                                                <PencilIcon className="w-4 h-4" />
                                                                                <span className="hidden sm:inline">Sign</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <MediaPreview
                                                                    items={guarantorPreviews[index]?.[r.value]}
                                                                    onRemove={(i) =>
                                                                        removeGuarantorMedia(index, r.value, i)
                                                                    }
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </fieldset>
                                        </>
                                    )}
                                    {guarantors.length > 1 && (
                                        <button type="button" onClick={() => removeGuarantor(index)} className="text-sm px-1.5 py-0.5 rounded-lg font-medium text-red-600 hover:bg-red-100 mt-2">
                                            Remove
                                        </button>
                                    )}
                                </fieldset>
                            ))}
                            <button
                                type="button"
                                onClick={addGuarantor}
                                className="mt-3 text-sm p-1.5 rounded-lg font-medium text-blue-600 hover:bg-blue-100"
                            >
                                + Add Guarantor
                            </button>
                        </>
                    )}
                </div>
                <div className="border-t pt-3 flex items-center justify-between">
                    <button
                        type='button'
                        onClick={() => handleAddClose()}
                        className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 transition"
                    >
                        Reset
                    </button>
                    {employeeChecked && (
                        <button
                            type="button"
                            onClick={() => {
                                setDialog({
                                    open: true,
                                    type: "warning",
                                    title: "Confirm Employee Creation",
                                    message: "Please confirm all information is correct before creating this employee record, including guarantor details (if applicable).",
                                    confirmText: "Confirm",
                                    action: () => handleSubmit(),
                                })
                            }}
                            className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                            Create
                        </button>
                    )}
                </div>
            </div>
        </>
    )
}

export default EmployeeAdd;